import React, { useState, useEffect } from 'react';
import { 
  FaFlag, 
  FaCheck, 
  FaTimes, 
  FaBan, 
  FaSearch, 
  FaFilter, 
  FaCalendarAlt, 
  FaExclamationTriangle,
  FaCheckCircle,
  FaEye,
} from 'react-icons/fa';
import api from '../../services/api';

const ReportsManagement = () => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [message, setMessage] = useState({ text: '', type: '', show: false });
    const [selectedReport, setSelectedReport] = useState(null);

    useEffect(() => {
        fetchReports();
    }, []);

    const showMessage = (text, type) => {
        setMessage({ text, type, show: true });
        setTimeout(() => {
            setMessage({ text: '', type: '', show: false });
        }, 3000);
    };

    const fetchReports = async () => {
        try {
            const res = await api.get('/admin/reports');
            setReports(res.data);
        } catch (error) {
            console.error('Failed to fetch reports', error);
            showMessage('Failed to load reports', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleAction = async (reportId, action) => {
        try {
            await api.post('/admin/reports/action', { reportId, action });
            showMessage(`Report ${action === 'dismiss' ? 'dismissed' : 'resolved'} successfully`, 'success');
            fetchReports();
            setSelectedReport(null);
        } catch (error) {
            showMessage('Failed to update report status', 'error');
        }
    };

    const getStatusColor = (status) => {
        switch(status) {
            case 'pending': return 'bg-yellow-100 text-yellow-700';
            case 'resolved': return 'bg-green-100 text-green-700';
            case 'dismissed': return 'bg-gray-100 text-gray-700';
            default: return 'bg-blue-100 text-blue-700';
        }
    };

    const filteredReports = reports.filter(report => {
        const matchesSearch = searchTerm === '' || 
            report.reporterId?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            report.reportedProfileId?.basicInfo?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            report.reason?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesFilter = statusFilter === 'all' || report.status === statusFilter;
        
        return matchesSearch && matchesFilter;
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mb-4"></div>
                    <p className="text-gray-600 text-lg">Loading reports...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            {/* Message Popup */}
            {message.show && (
                <div className={`fixed top-4 right-4 z-50 transform transition-all duration-300 ${
                    message.type === 'success' ? 'bg-blue-500' : 'bg-gray-400'
                } text-white px-6 py-3 rounded-xl shadow-2xl flex items-center space-x-3 animate-slide-in`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        message.type === 'success' ? 'bg-blue-600' : 'bg-gray-500'
                    }`}>
                        {message.type === 'success' ? '✓' : '✗'}
                    </div>
                    <span className="font-medium">{message.text}</span>
                    <button onClick={() => setMessage({ ...message, show: false })} className="ml-4 hover:opacity-80">
                        <FaTimes />
                    </button>
                </div>
            )}

            {/* Header */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800 flex items-center">
                            <FaFlag className="mr-3 text-blue-500" />
                            Reports Management
                        </h1>
                        <p className="text-gray-600 mt-1">Review and manage user-submitted reports</p>
                    </div>
                    <div className="flex items-center space-x-3">
                        <button 
                            onClick={fetchReports}
                            className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors duration-200 flex items-center"
                        >
                            <FaFilter className="mr-2" /> Refresh
                        </button>
                        <div className="px-4 py-2.5 bg-blue-500 text-white rounded-xl font-medium">
                            {reports.length} Total
                        </div>
                    </div>
                </div>

                {/* Search & Filters */}
                <div className="flex flex-col md:flex-row gap-4 mt-6">
                    {/* Search */}
                    <div className="relative flex-1">
                        <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by user name or reason..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    {/* Filter Buttons */}
                    <div className="flex gap-2">
                        <button
                            onClick={() => setStatusFilter('all')}
                            className={`px-4 py-3 rounded-xl font-medium transition-colors ${
                                statusFilter === 'all' 
                                    ? 'bg-blue-500 text-white' 
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            All Reports
                        </button>
                        <button
                            onClick={() => setStatusFilter('pending')}
                            className={`px-4 py-3 rounded-xl font-medium transition-colors ${
                                statusFilter === 'pending' 
                                    ? 'bg-yellow-500 text-white' 
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            Pending
                        </button>
                        <button
                            onClick={() => setStatusFilter('resolved')}
                            className={`px-4 py-3 rounded-xl font-medium transition-colors ${
                                statusFilter === 'resolved' 
                                    ? 'bg-green-500 text-white' 
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            Resolved
                        </button>
                    </div>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Total Reports</p>
                            <p className="text-2xl font-bold text-gray-800 mt-1">{reports.length}</p>
                        </div>
                        <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                            <FaFlag className="text-blue-500 text-xl" />
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Pending</p>
                            <p className="text-2xl font-bold text-gray-800 mt-1">
                                {reports.filter(r => r.status === 'pending').length}
                            </p>
                        </div>
                        <div className="w-12 h-12 rounded-xl bg-yellow-100 flex items-center justify-center">
                            <FaExclamationTriangle className="text-yellow-500 text-xl" />
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Resolved</p>
                            <p className="text-2xl font-bold text-gray-800 mt-1">
                                {reports.filter(r => r.status === 'resolved').length}
                            </p>
                        </div>
                        <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                            <FaCheckCircle className="text-green-500 text-xl" />
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Dismissed</p>
                            <p className="text-2xl font-bold text-gray-800 mt-1">
                                {reports.filter(r => r.status === 'dismissed').length}
                            </p>
                        </div>
                        <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
                            <FaTimes className="text-gray-500 text-xl" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Reports Table */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-8 py-5 font-semibold text-gray-600 text-sm uppercase tracking-wider">Report Details</th>
                                <th className="px-8 py-5 font-semibold text-gray-600 text-sm uppercase tracking-wider">Users</th>
                                <th className="px-8 py-5 font-semibold text-gray-600 text-sm uppercase tracking-wider">Date & Time</th>
                                <th className="px-8 py-5 font-semibold text-gray-600 text-sm uppercase tracking-wider">Status</th>
                                <th className="px-8 py-5 font-semibold text-gray-600 text-sm uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filteredReports.length > 0 ? filteredReports.map((report) => (
                                <tr 
                                    key={report._id} 
                                    className="hover:bg-gray-50/80 transition-colors duration-150"
                                >
                                    <td className="px-8 py-5">
                                        <div className="space-y-2">
                                            <h3 className="font-medium text-gray-800">{report.reason}</h3>
                                          
                                        </div>
                                    </td>
                                    <td className="px-8 py-5">
                                        <div className="space-y-3">
                                            <div className="flex items-center">
                                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-500 flex items-center justify-center text-white text-xs font-bold mr-2">
                                                    {report.reporterId?.name?.[0] || 'U'}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium text-gray-800">{report.reporterId?.name || 'Unknown'}</p>
                                                    <p className="text-xs text-gray-500">Reporter</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center">
                                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-400 to-red-500 flex items-center justify-center text-white text-xs font-bold mr-2">
                                                    {report.reportedProfileId?.basicInfo?.firstName?.[0] || 'U'}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium text-gray-800">{report.reportedProfileId?.basicInfo?.firstName || 'Unknown'} {report.reportedProfileId?.basicInfo?.lastName || ''}</p>
                                                    <p className="text-xs text-gray-500">Reported User</p>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-8 py-5">
                                        <div className="space-y-1">
                                            <div className="flex items-center text-sm text-gray-600">
                                                <FaCalendarAlt className="mr-2 text-gray-400" />
                                                {new Date(report.createdAt).toLocaleDateString()}
                                            </div>
                                            <div className="text-sm text-gray-500">
                                                {new Date(report.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-8 py-5">
                                        <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${getStatusColor(report.status)}`}>
                                            {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                                        </span>
                                    </td>
                                    <td className="px-8 py-5">
                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={() => setSelectedReport(report)}
                                                className="p-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl transition-colors duration-200"
                                                title="View Details"
                                            >
                                                <FaEye />
                                            </button>
                                            {report.status === 'pending' && (
                                                <>
                                                    <button
                                                        onClick={() => handleAction(report._id, 'resolve')}
                                                        className="p-2.5 bg-green-100 hover:bg-green-200 text-green-600 rounded-xl transition-colors duration-200"
                                                        title="Resolve Report"
                                                    >
                                                        <FaCheck />
                                                    </button>
                                                    <button
                                                        onClick={() => handleAction(report._id, 'dismiss')}
                                                        className="p-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl transition-colors duration-200"
                                                        title="Dismiss Report"
                                                    >
                                                        <FaTimes />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="5" className="text-center py-16">
                                        <div className="flex flex-col items-center">
                                            <FaFlag className="text-6xl text-gray-300 mb-4" />
                                            <p className="text-gray-600 text-lg font-medium">No reports found</p>
                                            <p className="text-gray-500 mt-1">Try adjusting your search or filter</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer */}
                <div className="px-8 py-5 border-t border-gray-200 bg-gray-50">
                    <div className="flex justify-between items-center">
                        <p className="text-gray-600">
                            Showing <span className="font-semibold">{filteredReports.length}</span> of <span className="font-semibold">{reports.length}</span> reports
                        </p>
                        <div className="flex items-center space-x-4">
                            <span className="text-sm text-gray-500">
                                Last updated: {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Report Detail Modal */}
            {selectedReport && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-6 rounded-t-2xl">
                            <div className="flex justify-between items-center">
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-800">Report Details</h2>
                                    <p className="text-gray-600 mt-1">ID: {selectedReport._id}</p>
                                </div>
                                <button
                                    onClick={() => setSelectedReport(null)}
                                    className="p-2.5 hover:bg-gray-100 rounded-xl transition-colors duration-200"
                                >
                                    <FaTimes className="text-gray-500 text-xl" />
                                </button>
                            </div>
                        </div>

                        <div className="px-8 py-6 space-y-6">
                            {/* Report Info */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="bg-gray-50 rounded-xl p-5">
                                    <h3 className="font-semibold text-gray-700 mb-3">Reporter</h3>
                                    <div className="flex items-center">
                                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-500 flex items-center justify-center text-white text-lg font-bold mr-3">
                                            {selectedReport.reporterId?.name?.[0] || 'U'}
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-800">{selectedReport.reporterId?.name || 'Unknown'}</p>
                                            <p className="text-sm text-gray-500">{selectedReport.reporterId?.email || 'No email'}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-gray-50 rounded-xl p-5">
                                    <h3 className="font-semibold text-gray-700 mb-3">Reported User</h3>
                                    <div className="flex items-center">
                                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-400 to-red-500 flex items-center justify-center text-white text-lg font-bold mr-3">
                                            {selectedReport.reportedProfileId?.basicInfo?.firstName?.[0] || 'U'}
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-800">
                                                {selectedReport.reportedProfileId?.basicInfo?.firstName || 'Unknown'} {selectedReport.reportedProfileId?.basicInfo?.lastName || ''}
                                            </p>
                                            <p className="text-sm text-gray-500">
                                                {selectedReport.reportedProfileId?.basicInfo?.gender || 'Unknown gender'}, {selectedReport.reportedProfileId?.basicInfo?.age || 'Unknown age'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Report Content */}
                            <div className="bg-gray-50 rounded-xl p-5">
                                <h3 className="font-semibold text-gray-700 mb-3">Report Information</h3>
                                <div className="space-y-4">
                                    <div>
                                        <p className="text-sm text-gray-500 mb-1">Reason</p>
                                        <p className="text-gray-800 font-medium">{selectedReport.reason}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500 mb-1">Details</p>
                                        <p className="text-gray-800">{selectedReport.details || 'No additional details provided'}</p>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <p className="text-sm text-gray-500">Date Reported</p>
                                            <p className="text-gray-800">{new Date(selectedReport.createdAt).toLocaleString()}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">Status</p>
                                            <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${getStatusColor(selectedReport.status)}`}>
                                                {selectedReport.status.charAt(0).toUpperCase() + selectedReport.status.slice(1)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                      
{/* Action Buttons in Modal */}
<div className="flex flex-wrap gap-3 pt-6 border-t border-gray-200">
    {selectedReport.status === 'pending' && (
        <>
            <button
                onClick={() => handleAction(selectedReport._id, 'resolve')}
                className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl font-medium transition-colors duration-200 flex items-center"
            >
                <FaCheck className="mr-2" /> Resolve Report
            </button>
            <button
                onClick={() => handleAction(selectedReport._id, 'dismiss')}
                className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-xl font-medium transition-colors duration-200 flex items-center"
            >
                <FaTimes className="mr-2" /> Dismiss Report
            </button>
            {/* NEW: Working Block User Button */}
           <button
    onClick={async () => {
        try {
            const reportedUserId = selectedReport.reportedProfileId?.userId || 
                                  selectedReport.reportedUserId;

            await api.post('/admin/reports/action', {  // ← இங்கதான் மாற்றம்: /status → /action
                userId: reportedUserId,
                action: 'block'
            });

            showMessage('User blocked successfully', 'success');
            fetchReports();
            setSelectedReport(null);
        } catch (err) {
            showMessage('Failed to block user', 'error');
        }
    }}
    className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl font-medium transition-colors duration-200 flex items-center"
>
    <FaBan className="mr-2" /> Block User
</button>
        </>
    )}
    <button
        onClick={() => setSelectedReport(null)}
        className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors duration-200"
    >
        Close
    </button>
</div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReportsManagement;